/**
 * Comments & suggestions panel of a page (Google-Docs-style review flow).
 *
 * Lists the page's threads (open first), lets you start a comment on the
 * pending selection, reply, resolve/reopen, and accept/reject suggestions.
 * URL-addressable (`?comments=1`) like the other page overlays.
 */
import { createEffect, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  Check,
  MessageSquare,
  MessageSquareText,
  PenLine,
  RotateCcw,
  Trash2,
  X,
} from "@fleet/icons";
import type { CommentThread } from "@folio/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { formatRelativeTime } from "@/features/notes/lib/format";
import type { usePageComments } from "../hooks/use-page-comments";

interface CommentsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  comments: ReturnType<typeof usePageComments>;
  /** Anchor text of a pending "new comment" (selection captured on open). */
  pendingAnchor: () => string | null;
  onPendingDone: () => void;
  /** Thread to scroll to / highlight (click on a mark in the editor). */
  focusedThreadId: () => string | null;
}

function ThreadKindBadge(props: { thread: CommentThread }) {
  const { t } = useI18n();
  return (
    <Show
      when={props.thread.kind === "suggestion"}
      fallback={
        <Badge variant="secondary" class="gap-1">
          <MessageSquare class="h-3 w-3" />
          {t(msg`Comment`)}
        </Badge>
      }
    >
      <Badge variant="outline" class="gap-1 border-accent/40 text-accent">
        <PenLine class="h-3 w-3" />
        <Show
          when={props.thread.suggestion?.action === "delete"}
          fallback={t(msg`Suggested insertion`)}
        >
          {t(msg`Suggested deletion`)}
        </Show>
      </Badge>
    </Show>
  );
}

export function CommentsPanel(props: CommentsPanelProps) {
  const { t, locale } = useI18n();
  const [composerText, setComposerText] = createSignal("");
  const [replyDrafts, setReplyDrafts] = createSignal<Record<string, string>>({});
  let listRef: HTMLDivElement | undefined;

  const replyDraft = (id: string) => replyDrafts()[id] ?? "";
  const setReplyDraft = (id: string, value: string) => {
    setReplyDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const submitComposer = async () => {
    try {
      const ok = await props.comments.addComment(composerText());
      if (ok) {
        setComposerText("");
        props.onPendingDone();
      }
    } catch (err: unknown) {
      console.error("Error submitting comment:", err);
    }
  };

  const submitReply = async (thread: CommentThread) => {
    const text = replyDraft(thread.id);
    if (text.trim() === "") {
      return;
    }
    try {
      await props.comments.reply(thread, text);
      setReplyDraft(thread.id, "");
    } catch (err: unknown) {
      console.error("Error submitting reply:", err);
    }
  };

  const handleReview = async (thread: CommentThread, accept: boolean) => {
    try {
      await props.comments.review(thread, accept);
    } catch (err: unknown) {
      console.error("Error reviewing suggestion:", err);
    }
  };

  const handleResolve = async (thread: CommentThread) => {
    try {
      await props.comments.resolve(thread);
    } catch (err: unknown) {
      console.error("Error resolving thread:", err);
    }
  };

  const handleReopen = async (thread: CommentThread) => {
    try {
      await props.comments.reopen(thread);
    } catch (err: unknown) {
      console.error("Error reopening thread:", err);
    }
  };

  // Scroll the focused thread into view when the panel opens on a mark click.
  createEffect(() => {
    const id = props.focusedThreadId();
    if (!props.open || !id) {
      return;
    }
    queueMicrotask(() => {
      listRef
        ?.querySelector(`[data-thread-id="${id}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-lg">
      <div class="flex max-h-[70vh] min-h-[40vh] flex-col gap-3 p-1" data-testid="comments-panel">
        <div class="flex items-center gap-2">
          <MessageSquareText class="h-4 w-4 text-accent" />
          <DialogTitle>{t(msg`Comments & suggestions`)}</DialogTitle>
        </div>

        {/* ── New comment composer (a selection is pending) ─────────── */}
        <Show when={props.pendingAnchor()}>
          {(anchor) => (
            <div class="rounded-lg border border-accent/30 bg-accent/5 p-3">
              <p class="mb-2 line-clamp-2 border-l-2 border-accent/50 pl-2 text-xs italic text-muted-foreground">
                {anchor()}
              </p>
              <textarea
                value={composerText()}
                onInput={(e) => setComposerText(e.currentTarget.value)}
                placeholder={t(msg`Write a comment…`)}
                rows={2}
                class="w-full resize-none rounded-md border border-border/60 bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-accent"
                data-testid="comment-composer-input"
              />
              <div class="mt-2 flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="comment-composer-cancel"
                  onClick={() => {
                    setComposerText("");
                    props.onPendingDone();
                  }}
                >
                  {t(msg`Cancel`)}
                </Button>
                <Button
                  size="sm"
                  data-testid="comment-composer-submit"
                  disabled={composerText().trim() === ""}
                  onClick={() => {
                    void submitComposer();
                  }}
                >
                  {t(msg`Comment`)}
                </Button>
              </div>
            </div>
          )}
        </Show>

        {/* ── Threads ───────────────────────────────────────────────── */}
        <div ref={(el) => (listRef = el)} class="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          <Show
            when={props.comments.threads().length > 0 || props.pendingAnchor()}
            fallback={
              <EmptyState
                icon={MessageSquare}
                title={t(msg`No comments yet`)}
                description={t(
                  msg`Select some text and choose "Comment", or turn on suggestion mode to propose edits.`,
                )}
              />
            }
          >
            <For each={props.comments.threads()}>
              {(thread) => (
                <div
                  data-testid="comment-thread"
                  data-thread-id={thread.id}
                  data-thread-status={thread.status}
                  class="rounded-lg border p-3 transition-colors"
                  classList={{
                    "border-border/60": props.focusedThreadId() !== thread.id,
                    "border-accent ring-1 ring-accent/40": props.focusedThreadId() === thread.id,
                    "opacity-70": thread.status === "resolved",
                  }}
                >
                  <div class="flex flex-wrap items-center gap-2">
                    <ThreadKindBadge thread={thread} />
                    <Show when={thread.status === "resolved"}>
                      <Badge variant="outline" class="gap-1 text-muted-foreground">
                        <Check class="h-3 w-3" />
                        <Show when={thread.outcome === "accepted"}>{t(msg`Accepted`)}</Show>
                        <Show when={thread.outcome === "rejected"}>{t(msg`Rejected`)}</Show>
                        <Show when={!thread.outcome}>{t(msg`Resolved`)}</Show>
                      </Badge>
                    </Show>
                    <span class="ml-auto text-[11px] text-muted-foreground">
                      {formatRelativeTime(thread.createdAt, locale())}
                    </span>
                  </div>

                  <Show when={props.comments.liveAnchorText(thread)}>
                    {(anchor) => (
                      <p class="mt-2 line-clamp-2 border-l-2 border-accent/40 pl-2 text-xs italic text-muted-foreground">
                        {anchor()}
                      </p>
                    )}
                  </Show>

                  <div class="mt-2 space-y-2">
                    <For each={thread.messages}>
                      {(message) => (
                        <div class="text-sm">
                          <span class="font-medium">{message.authorName}</span>
                          <span class="ml-2 text-[11px] text-muted-foreground">
                            {formatRelativeTime(message.createdAt, locale())}
                          </span>
                          <p class="whitespace-pre-wrap text-foreground/90">{message.text}</p>
                        </div>
                      )}
                    </For>
                  </div>

                  {/* Reply (open threads) */}
                  <Show when={thread.status === "open"}>
                    <div class="mt-2 flex items-center gap-2">
                      <input
                        type="text"
                        value={replyDraft(thread.id)}
                        onInput={(e) => setReplyDraft(thread.id, e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void submitReply(thread);
                          }
                        }}
                        placeholder={t(msg`Reply…`)}
                        class="min-w-0 flex-1 rounded-md border border-border/60 bg-background px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-accent"
                        data-testid="thread-reply-input"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="thread-reply-send"
                        disabled={replyDraft(thread.id).trim() === ""}
                        onClick={() => {
                          void submitReply(thread);
                        }}
                      >
                        {t(msg`Reply`)}
                      </Button>
                    </div>
                  </Show>

                  {/* Actions */}
                  <div class="mt-2 flex flex-wrap items-center gap-1.5">
                    <Show when={thread.kind === "suggestion" && thread.status === "open"}>
                      <Button
                        size="sm"
                        class="gap-1"
                        data-testid="suggestion-accept"
                        onClick={() => {
                          void handleReview(thread, true);
                        }}
                      >
                        <Check class="h-3.5 w-3.5" />
                        {t(msg`Accept`)}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        class="gap-1"
                        data-testid="suggestion-reject"
                        onClick={() => {
                          void handleReview(thread, false);
                        }}
                      >
                        <X class="h-3.5 w-3.5" />
                        {t(msg`Reject`)}
                      </Button>
                    </Show>
                    <Show when={thread.kind === "comment" && thread.status === "open"}>
                      <Button
                        variant="outline"
                        size="sm"
                        class="gap-1"
                        data-testid="thread-resolve"
                        onClick={() => {
                          void handleResolve(thread);
                        }}
                      >
                        <Check class="h-3.5 w-3.5" />
                        {t(msg`Resolve`)}
                      </Button>
                    </Show>
                    <Show when={thread.kind === "comment" && thread.status === "resolved"}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1"
                        data-testid="thread-reopen"
                        onClick={() => {
                          void handleReopen(thread);
                        }}
                      >
                        <RotateCcw class="h-3.5 w-3.5" />
                        {t(msg`Reopen`)}
                      </Button>
                    </Show>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="ml-auto gap-1 text-muted-foreground hover:text-danger"
                      data-testid="thread-delete"
                      aria-label={t(msg`Delete thread`)}
                      onClick={() => {
                        void props.comments.remove(thread);
                      }}
                    >
                      <Trash2 class="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </For>
          </Show>
        </div>
      </div>
    </Dialog>
  );
}
